import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {usePaymentPreview} from '../../../../../features/CatalogGrid/hooks/usePaymentPreview'
import {useUserFeatures} from '../../../../../context/user-features-context'
import {getIsNewTLMCAccessible} from '../../../../../features/CatalogGrid/utils/productSelectionRules'
import {
  selectProductConfigInfo,
  selectProposalInfo,
  updateOption,
} from '../../../../../app-store/ProposalSlice'
import {calculateCouponsAmountForItem} from '../../../../../utils/coupon-helpers'

// Refreshes live due-today pricing (fetched from Recurly) for every option in
// the proposal and writes it into Redux, so the client-facing ProposalPreview
// popup shows an up-to-date option.dueTodayTotal and item.dueTodayAmount.
// monthlyTotal/recurringAmount are DB-sourced (set on save) and are never
// touched by this refresh.
//
// Backend-persisted coupons only carry { couponCode, couponName }. This hook
// hydrates them via orders/coupon/validate to get full discount definitions
// before applying calculations, so previews are accurate whether the proposal
// was just built or loaded from the dashboard.
//
// Wired to the Preview, Save, and Send Proposal actions. Returns the merged
// proposalInfo synchronously so callers that immediately need the fresh
// values (e.g. the Save/Send API calls) don't have to wait for a re-render.
export function useProposalPaymentPreview() {
  const dispatch = useDispatch()
  const client = useAuthClient()
  const {previewPayment} = usePaymentPreview()
  const proposalInfo = useSelector(selectProposalInfo)
  const productConfigInfo = useSelector(selectProductConfigInfo)

  // Mirror the same flags the legacy CC-upgrade-cart preview payload used
  // to gate the TL-growth-package and BC/MC-listings exclusion rules.
  const userFeatures = useUserFeatures()
  const isNewTLMCAccessible = getIsNewTLMCAccessible(userFeatures)
  const {Listings_BC_MC: frontendListingsBcMc} = useUserFeatures()

  const refreshPaymentPreview = useCallback(
    async (displayOrder = null) => {
      const {thryvId, country, options} = proposalInfo

      // When a displayOrder is provided, only that option is refreshed — avoids
      // N parallel API calls when a single option's items change (add/remove
      // product). Callers that need all options refreshed omit the argument
      // (on-load, coupon change, save, send).
      const optionsToRefresh =
        displayOrder != null
          ? options.filter(o => o.displayOrder === displayOrder)
          : options

      // Fetches a full coupon definition for coupons saved in backend shape
      // { couponCode, couponName }. Coupons already in builder-session shape
      // (have a `code` field) are returned unchanged.
      const hydrateCoupon = async (coupon, productCodesStr) => {
        if (coupon.code) return coupon
        const code = coupon.couponCode
        if (!code) return null
        try {
          const resp = await client(
            `orders/coupon/validate?couponCode=${code}&country=${country}&addonsString=${productCodesStr}`,
          )
          return resp?.isValid ? resp : null
        } catch {
          return null
        }
      }

      const refreshed = await Promise.all(
        optionsToRefresh.map(async option => {
          const rawCoupons = option.coupons ?? []
          const productCodesStr = (option.items ?? [])
            .map(i => i.productCode)
            .join(',')

          // Hydrate backend-shape coupons before calculating discounts, then
          // write the resolved definitions back into Redux so ProposalPreview
          // and OrderSummary both see full coupon objects.
          const needsHydration = rawCoupons.some(c => !c.code && c.couponCode)
          let resolvedCoupons = rawCoupons
          if (needsHydration) {
            resolvedCoupons = (
              await Promise.all(
                rawCoupons.map(c => hydrateCoupon(c, productCodesStr)),
              )
            ).filter(Boolean)
            dispatch(
              updateOption({
                displayOrder: option.displayOrder,
                changes: {coupons: resolvedCoupons},
                skipDirty: true,
              }),
            )
          }

          const validCoupons = resolvedCoupons.filter(c => c.isValid)

          // EXISTING client: backend computes discounted totals via Recurly.
          // If billing info is missing (account created but order never
          // completed), fall through to the new-client calculation so coupons
          // still apply correctly.
          if (thryvId) {
            try {
              const couponCodes = validCoupons.map(c => c.code)
              const result = await previewPayment({
                items: option.items,
                thryvId,
                country,
                coupons: couponCodes,
                productConfigInfo,
                isNewTLMCAccessible,
                frontendListingsBcMc,
              })
              if (!result) return option

              dispatch(
                updateOption({
                  displayOrder: option.displayOrder,
                  changes: {...result, dueTodayIncludesTax: country === 'AU'},
                  skipDirty: true,
                }),
              )
              return {
                ...option,
                ...result,
                dueTodayIncludesTax: country === 'AU',
              }
            } catch (err) {
              if (err.message !== 'Billing info missing on account')
                return option
              // Fall through to new-client calculation below.
            }
          }

          // NEW client path (no thryvId) OR billing info missing on account:
          // Compute dueTodayAmount per item client-side by subtracting coupon
          // discounts from unitPrice, matching the shape the API path produces.
          const updatedItems = (option.items ?? []).map(item => {
            if (item.alreadyIncluded) return {...item, dueTodayAmount: 0}
            const prodType =
              item.productType === 'ONE_TIME_FEE'
                ? 'oneTimeFee'
                : item.productType === 'APP'
                ? 'itemBasedAddon'
                : 'plan'
            const lineTotal =
              (item.unitPrice ?? item.price ?? 0) * (item.quantity ?? 1)
            const {discountAmount} = calculateCouponsAmountForItem(
              {
                code: item.productCode,
                prodType,
                currency: {unitAmount: lineTotal},
                quantity: 1,
              },
              validCoupons,
            )
            return {
              ...item,
              dueTodayAmount: Math.max(lineTotal - discountAmount, 0),
            }
          })
          const dueTodayTotal = updatedItems.reduce(
            (sum, i) => sum + i.dueTodayAmount,
            0,
          )
          const result = {
            items: updatedItems,
            dueTodayTotal,
            dueTodayIncludesTax: false,
          }
          dispatch(
            updateOption({
              displayOrder: option.displayOrder,
              changes: result,
              skipDirty: true,
            }),
          )
          return {...option, ...result}
        }),
      )

      // Merge refreshed results back into the full options list so the return
      // value is always complete regardless of whether displayOrder filtered it.
      const refreshedByOrder = new Map(refreshed.map(o => [o.displayOrder, o]))
      const updatedOptions = options.map(
        o => refreshedByOrder.get(o.displayOrder) ?? o,
      )

      return {...proposalInfo, options: updatedOptions}
    },
    [
      proposalInfo,
      client,
      previewPayment,
      dispatch,
      productConfigInfo,
      isNewTLMCAccessible,
      frontendListingsBcMc,
    ],
  )

  return {refreshPaymentPreview}
}
